<template>
  <modal-vue
    :modal-active="modelValue"
    max-width="max-w-[700px]"
    @close="$emit('update:modelValue', false)"
  >
    <template #header>
      <div class="flex items-center gap-3 w-full">
        <h2 class="text-lg font-bold text-secondary50">Detalhes de aprovação de trocas</h2>
        <HandoverStatusBadge v-if="item" :status="item.package.status" />
      </div>
    </template>

    <template v-if="item">
      <div class="flex items-start justify-start gap-8 py-4">
        <div class="flex flex-col gap-1.5">
          <span class="text-xs text-[#7f7f8e] text-left">Passou</span>
          <DoctorInfo
            :name="item.package.requester.name"
            :subtitle="`CRM ${item.package.requester.crm}`"
            :avatar-url="item.package.requester.avatarUrl"
            :nps-score="item.package.requester.npsScore"
            size="xl"
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <span class="text-xs text-[#7f7f8e] text-left">Recebeu</span>
          <DoctorInfo
            :name="destinyName"
            :subtitle="destinyCrm"
            :avatar-url="destinyDoctor?.avatarUrl"
            :nps-score="destinyDoctor?.npsScore"
            size="xl"
          />
        </div>
      </div>

      <div class="flex flex-col border border-[#e8e8ed] rounded-lg divide-y divide-[#e8e8ed]">
        <div class="flex items-center justify-between px-4 py-2.5">
          <span class="text-sm text-[#7f7f8e]">Unidade</span>
          <span class="text-sm font-semibold text-[#16161d]">{{ healthUnitName }}</span>
        </div>
        <div class="flex items-center justify-between px-4 py-2.5">
          <span class="text-sm text-[#7f7f8e]">Escala</span>
          <span class="text-sm font-semibold text-[#16161d]">{{ scheduleName }}</span>
        </div>
        <div class="flex items-center justify-between px-4 py-2.5">
          <span class="text-sm text-[#7f7f8e]">Data de criação</span>
          <span class="text-sm font-semibold text-[#16161d]">{{ formattedCreatedAt }}</span>
        </div>
        <div class="flex items-center justify-between px-4 py-2.5">
          <span class="text-sm text-[#7f7f8e]">Aprovado por</span>
          <span class="text-sm font-semibold text-[#16161d]">
            {{ item.reviewerName ? getFirstAndSecondName(item.reviewerName) : '-' }}
          </span>
        </div>
        <div class="flex items-center justify-between px-4 py-2.5">
          <span class="text-sm text-[#7f7f8e]">Valor estimado total</span>
          <span class="text-sm font-semibold text-[#16161d]">{{ formattedValue }}</span>
        </div>
      </div>

      <div class="flex flex-col gap-3 mt-5">
        <div class="flex items-center justify-between">
          <span class="text-sm font-semibold text-[#16161d]">Plantões inclusos</span>
          <span
            class="text-xs font-semibold text-primary30 bg-white border border-primary30 rounded px-2 py-1"
          >
            {{ item.package.stations.length }} Plantões na troca
          </span>
        </div>

        <div class="w-full overflow-x-auto border border-[#d8d8e2] rounded-lg">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-[#f0f0f4]">
                <th class="px-4 py-2 text-xs font-bold text-[#272735] text-center">Data</th>
                <th class="px-4 py-2 text-xs font-bold text-[#272735] text-center">Posição</th>
                <th class="px-4 py-2 text-xs font-bold text-[#272735] text-center">Turno</th>
                <th class="px-4 py-2 text-xs font-bold text-[#272735] text-center">Valor</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="station in item.package.stations"
                :key="station.id"
                class="border-t border-[#e8e8ed]"
              >
                <td class="px-4 py-2.5 text-[13px] text-[#16161d] text-center">
                  {{ formatDate(station.date) }}
                </td>
                <td class="px-4 py-2.5 text-[13px] text-[#16161d] text-center">
                  {{ station.position ?? '-' }}
                </td>
                <td class="px-4 py-2.5 text-[13px] text-[#16161d] text-center">
                  {{ station.type ?? '-' }}
                </td>
                <td class="px-4 py-2.5 text-[13px] text-[#16161d] text-center">
                  {{ formatCurrency(station.value ?? 0) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-center">
        <FilledButton
          text="Voltar para o histórico de aprovação de trocas"
          width="100%"
          height="44px"
          class="bg-primary30"
          data-testid="detail-back-button"
          @click="$emit('update:modelValue', false)"
        />
      </div>
    </template>
  </modal-vue>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { format, parseISO } from 'date-fns'
import type { AdminQueueResponse } from '@/Network/Types/Responses/HandoverInviteResponses'
import { formatCurrency } from '@/utils/formatters'
import { getFirstAndSecondName } from '@/utils/getFirstAndSecondName'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import HandoverStatusBadge from './HandoverStatusBadge.vue'
import DoctorInfo from '@/components/DoctorInfo.vue'

const props = defineProps<{
  modelValue: boolean
  item: AdminQueueResponse | null
}>()

defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const formatDate = (dateStr: string) => {
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy')
  } catch {
    return dateStr
  }
}

const formatDateTime = (dateStr: string) => {
  try {
    return format(parseISO(dateStr), "dd/MM/yyyy 'às' HH:mm")
  } catch {
    return dateStr
  }
}

const destinyDoctor = computed(() => {
  const invite = props.item?.currentCandidate ?? props.item?.package.invites[0]
  return invite?.doctor ?? null
})
const destinyName = computed(() => destinyDoctor.value?.name ?? '-')
const destinyCrm = computed(() => {
  const crm = destinyDoctor.value?.crm
  return crm ? `CRM ${crm}` : ''
})

const healthUnitName = computed(() => props.item?.package.stations[0]?.healthUnit.name ?? '-')
const scheduleName = computed(() => props.item?.package.schedule.name ?? '-')
const formattedCreatedAt = computed(() =>
  props.item ? formatDateTime(props.item.package.createdAt) : '-'
)
const formattedValue = computed(() => {
  const total = (props.item?.package.stations ?? []).reduce((sum, s) => sum + (s.value ?? 0), 0)
  return formatCurrency(total)
})
</script>
